import {GridBasis} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridBasis';
import {GridCellKind} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridCellKind';
import {GridEvent} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridEvent';
import {GridRendererRegistry} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridRendererRegistry';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {IDisposable} from '@mintdata/mint-kit/api/resources/IDisposable';
import * as React from 'react';

export enum CellEventType
{
    Clicked,
    MouseEnter,
    MouseExit
}

export class CellEvent
{
    constructor(eventType:CellEventType)
    {
    }
}

export type OnCellEvent = (e:CellEvent) => (void);

export class CellRenderContext
{
    public constructor(public readonly isForMeasurement:boolean,
                       public readonly cell:IGridCell,
                       public readonly key:string,
                       public readonly style:any,
                       public readonly onCellEvent:OnCellEvent,
                       public readonly className?:string,
                       public readonly ref?:any,)
    {
    }
}

export interface IGridCellRenderer
{
    render(context:CellRenderContext):React.ReactElement<any>;
}

export interface IGridCell
{
    getKind():GridCellKind;

    isEqualTo(other:IGridCell):boolean;
}

export interface IGridHeaderCell extends IGridCell
{
    getBasis():GridBasis;
}

export interface IGridCellAreaStyles
{
    getCellClassNames(cellArea:CellArea, isRowSelected):string;
}

export type RowIndex = number;
export type ColumnIndex = number;
export type ColumnKey = string;
export type OnFillCell = (rowIndex:RowIndex, columnIndex:ColumnIndex, key:ColumnKey) => (IGridCell);
export type OnCreateHeader = (rowIndex:RowIndex, columnIndex:ColumnIndex, key:ColumnKey) => (IGridCell);
export type VisibleColumnKeys = ColumnKey[];
export type GridEventListener = (e:GridEvent) => (void);
export type OnGetCellArea = (rowIndex:RowIndex, columnIndex:number) => (CellArea);
export type OnGetToRowIndex = () => (RowIndex);
export type OnGetToColumnIndex = () => (ColumnIndex);

export interface IGridAppearance
{
    isColumnHeaderVisible:boolean;
    isRowHeaderVisible:boolean;
    hasVerticalLines:boolean;
    hasHorizontalLines:boolean;
    hasBorder:boolean;

    getCellAreaStyles():IGridCellAreaStyles;
    getRendererRegistry():GridRendererRegistry;
    getRendererFor(cell:IGridCell):IGridCellRenderer;
}

export class CellSize
{
    constructor(public readonly width:number,
                public readonly height:number)
    {
    }

    toString()
    {
        return `${this.width}x${this.height}`;
    }
}

export interface IMeasureBag
{
    getCellSize(rowIndex:RowIndex, columnIndex:ColumnIndex):CellSize;

    getWidestColumn(columnIndex:ColumnIndex):number

    getNumRows():number;

    getNumColumns():number;

    print():void;
}

export enum CellArea
{
    TopLeftCorner = 'TopLeftCorner',
    ColumnHeader = 'ColumnHeader',
    RowHeader = 'RowHeader',
    Body = 'Body'
}

export interface IGridModelView
{
    getNumRows():number;

    getNumColumns():number;

    getRow(rowIndex:RowIndex):IGridCell[];

    getColumn(columnIndex:ColumnIndex):IGridCell[];

    getCellAt(rowIndex:RowIndex, columnIndex:number):IGridCell;

    getAreaAt(rowIndex:RowIndex, columnIndex:number):CellArea;

    getAllRowsHeight():number;

    getRowHeight(rowIndex:RowIndex):number;

    setRowHeight(rowIndex:RowIndex, newHeight:number):void;

    isRowSelected(rowIndex:RowIndex):boolean;

    setIsRowSelected(rowIndex:RowIndex, isSelected:boolean):void;

    getAllColumnsWidth():number;

    getColumnWidth(columnIndex:ColumnIndex):number;

    setColumnWidth(columnIndex:ColumnIndex, newWidth:number):void;
}

export interface IGridModel
{
    getAppearance():IGridAppearance;

    isTransposed():boolean;

    getColumnFilter():VisibleColumnKeys;

    getColumnKeys():ColumnKey[];

    getRowHeaders():IGridModelView;

    getColumnHeaders():IGridModelView;

    getContentView():IGridModelView;

    getBody():IGridModelView;

    getBodyWithRowHeaders():IGridModelView;

    getBodyWithColumnHeaders():IGridModelView;

    getBodyWithBothHeaders():IGridModelView;

    getIndexOfColumn(key:ColumnKey, isRowHeaderColumnIncluded:boolean):ColumnIndex;

    getColumnKeyAtIndex(columnIndex:ColumnIndex, isRowHeaderColumnIncluded:boolean):ColumnKey;

    addListener(listener:GridEventListener):IDisposable;
}

export interface IMutableGridModel extends IGridModel
{
    getId():GUID;

    setIsTransposed(isTransposed:boolean);

    setColumnFilter(visibleColumns:VisibleColumnKeys);

    clear():void;

    clearDataRows():void;

    print():void;

    setCornerCell(cornerCell:IGridCell):void;

    appendColumns(columnKeys:ColumnKey[], createColumnHeader:OnCreateHeader, cellFiller:OnCreateHeader):void;

    appendDataRow(row:IGridCell[], createRowHeader?:OnCreateHeader):void;

    appendDataRowsAndColumns(rows:IGridCell[][], createRowHeader:OnCreateHeader,
                             newColumnKeys:ColumnKey[], createColumnHeader:OnCreateHeader, cellFiller:OnFillCell):void;
}
